import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import H2 from '@base/H2/H2';
import H3 from '@base/H3/H3';
import H5 from '@base/H5/H5';
import { mobile, Spacings, Fonts } from '@styles/index';
import { getImage } from '@utils/imageHelper';

const Wrapper = styled.div`
  position: relative;
  margin-top: 160px;
  padding: ${Spacings.SPACING_12B};
  text-align: center;
  background-image: linear-gradient(
    180deg,
    var(--color-i-blue-4) 0%,
    var(--color-i-blue-3) 100%
  );
  @media only screen and (max-width: ${mobile.maxWidth}) {
    margin-top: 120px;
    padding: ${Spacings.SPACING_12B} ${Spacings.SPACING_8B};
  }
  * {
    color: var(--color-i-white);
    position: relative;
    z-index: 3;
  }
  img {
    user-select: none;
    user-drag: none;
  }
`;

const BgGraph = styled.img`
  width: 100%;
  mix-blend-mode: ${({ $noBlend }) => ($noBlend ? 'none' : 'multiply')};
  position: absolute;
  left: 0;
  bottom: 0;
  z-index: 2;
  @media only screen and (max-width: ${mobile.maxWidth}) {
    height: 398px;
    object-fit: cover;
    object-position: 0px;
  }
`;

const Image = styled.img`
  margin: auto;
  display: block;
`;

const BadgesLogo = styled(Image)`
  height: 200px;
  @media only screen and (max-width: ${mobile.maxWidth}) {
    height: 120px;
  }
`;

const Title = styled(H2)`
  margin-top: ${Spacings.SPACING_6B};
  color: var(--color-i-white);
  @media only screen and (max-width: ${mobile.maxWidth}) {
    margin-top: ${Spacings.SPACING_8B};
    font-size: ${Fonts.H3};
  }
`;

const Container = styled.div`
  display: grid;
  grid-template: 1fr / 300px max-content 300px;
  grid-template-areas: 'left-content image right-content';
  align-items: center;
  justify-content: center;
  grid-gap: ${Spacings.SPACING_12B};
  margin-top: ${Spacings.SPACING_9B};
  @media only screen and (max-width: ${mobile.maxWidth}) {
    grid-template: max-content 1fr 1fr / 1fr;
    grid-template-areas: 'image' 'left-content' 'right-content';
  }
`;

const ContentContainer = styled.div`
  display: grid;
  grid-template: 1fr 1fr / 1fr;
  grid-row-gap: ${Spacings.SPACING_12B};
  grid-area: ${({ $gridArea }) => $gridArea};
`;

const FeatureContainer = styled.div``;

const FeatureTitle = styled(H3)`
  color: var(--color-i-white);
`;

const FeatureSubtitle = styled(H5)`
  margin-top: ${Spacings.SPACING_3B};
  font-weight: ${Fonts.REGULAR};
  color: var(--color-i-white);
`;

const PerformanceImage = styled(Image)`
  height: 500px;
  grid-area: image;
  @media only screen and (max-width: ${mobile.maxWidth}) {
    margin: 0px;
  }
`;

const FeaturePoints = ({ points, gridArea }) => {
  return (
    <ContentContainer $gridArea={gridArea}>
      {points?.map((point) => (
        <FeatureContainer key={point.id}>
          <FeatureTitle>{point.title}</FeatureTitle>
          <FeatureSubtitle>{point.subtitle}</FeatureSubtitle>
        </FeatureContainer>
      ))}
    </ContentContainer>
  );
};

FeaturePoints.propTypes = {
  points: PropTypes.arrayOf(PropTypes.object).isRequired,
  gridArea: PropTypes.string.isRequired
};

const CombatAlgo = ({ goalUID }) => {
  const algorithmData = useSelector(
    ({ combats }) => combats.staticData[goalUID]?.algorithmData || {}
  );
  const algorithmKeyPoints = useSelector(
    ({ combats }) => combats.staticData[goalUID]?.algorithmKeyPoints || []
  );
  const pointsHalfCount = Math.ceil(algorithmKeyPoints.length / 2 || 0);
  return (
    <Wrapper>
      <BgGraph src={getImage('combat/graph.svg')} alt="graph" />
      <BgGraph src={getImage('combat/graph.svg')} alt="graph" $noBlend />
      <BadgesLogo src={algorithmData.logo} alt="badges logo" />
      <Title>{algorithmData.title}</Title>
      <Container>
        <FeaturePoints
          gridArea="left-content"
          points={algorithmKeyPoints?.slice(0, pointsHalfCount)}
        />
        <PerformanceImage src={algorithmData.image} alt="performance" />
        <FeaturePoints
          gridArea="right-content"
          points={algorithmKeyPoints?.slice(pointsHalfCount)}
        />
      </Container>
    </Wrapper>
  );
};

CombatAlgo.propTypes = {
  goalUID: PropTypes.string.isRequired
};

export default CombatAlgo;
